import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Form } from "@telluscompany/design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useLocalization } from "@i18n";

import type { AdminStructureDetail } from "../../api/structures/structures.types";
import {
  usePatchAdminStructureGeneralDataMutation,
  useUploadAdminStructureOrthophotoMutation,
} from "../../api/structures/structures.mutations";
import {
  createAdminStructureGeneralDataSchema,
  mapDetailToGeneralDataForm,
  mapGeneralDataFormToPatchRequest,
  type AdminStructureGeneralDataForm,
} from "../../schemas";
import {
  AdminStructureGeneralDataFormContext,
  STRUCTURE_GENERAL_DATA_FORM_ID,
} from "./adminStructureGeneralDataForm.context.definition";

export type AdminStructureGeneralDataFormProviderProps = {
  complexId: string;
  structureId: string;
  canWrite: boolean;
  detail: AdminStructureDetail;
  onSaved: (detail: AdminStructureDetail) => void;
  children: ReactNode;
  /** false no modal multi-aba — cada aba tem `<form>` próprio; footer usa `form=`. */
  embedForm?: boolean;
};

export const AdminStructureGeneralDataFormProvider = ({
  complexId,
  structureId,
  canWrite,
  detail,
  onSaved,
  children,
  embedForm = true,
}: AdminStructureGeneralDataFormProviderProps) => {
  const { translate } = useLocalization();
  const patchMutation = usePatchAdminStructureGeneralDataMutation();
  const uploadMutation = useUploadAdminStructureOrthophotoMutation();
  const [pendingOrthophotoFile, setPendingOrthophotoFileState] =
    useState<File | null>(null);
  const pendingOrthophotoFileRef = useRef<File | null>(null);

  const setPendingOrthophotoFile = useCallback((file: File | null) => {
    pendingOrthophotoFileRef.current = file;
    setPendingOrthophotoFileState(file);
  }, []);

  const form = useForm<AdminStructureGeneralDataForm>({
    resolver: zodResolver(createAdminStructureGeneralDataSchema(translate)),
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: mapDetailToGeneralDataForm(detail),
  });

  useEffect(() => {
    form.reset(mapDetailToGeneralDataForm(detail));
  }, [detail, form]);

  useEffect(() => {
    setPendingOrthophotoFile(null);
  }, [structureId, setPendingOrthophotoFile]);

  const uploadPendingOrthophoto = useCallback(async () => {
    const file = pendingOrthophotoFileRef.current;
    if (!file) {
      return;
    }

    const result = await uploadMutation.mutateAsync({
      complexId,
      structureId,
      file,
    });
    onSaved({
      ...detail,
      orthophotoStoragePath: result.orthophotoStoragePath,
    });
    setPendingOrthophotoFile(null);
  }, [
    complexId,
    detail,
    onSaved,
    setPendingOrthophotoFile,
    structureId,
    uploadMutation,
  ]);

  const onInvalidSubmit = () => {
    void form.trigger(undefined, { shouldFocus: true });
  };

  const onSubmit = async (data: AdminStructureGeneralDataForm) => {
    const saved = await patchMutation.mutateAsync({
      complexId,
      structureId,
      payload: mapGeneralDataFormToPatchRequest(data),
    });
    onSaved(saved);
    form.reset(mapDetailToGeneralDataForm(saved));
  };

  const typeLocked = Boolean(detail.type);

  return (
    <AdminStructureGeneralDataFormContext.Provider
      value={{
        complexId,
        structureId,
        detail,
        canWrite,
        onSaved,
        typeLocked,
        embedForm,
        isSaving: patchMutation.isPending,
        isUploadingOrthophoto: uploadMutation.isPending,
        pendingOrthophotoFile,
        setPendingOrthophotoFile,
        uploadPendingOrthophoto,
        form,
        onSubmit,
        onInvalidSubmit,
      }}
    >
      <Form {...form}>
        {embedForm ? (
          <form
            id={STRUCTURE_GENERAL_DATA_FORM_ID}
            onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)}
            noValidate
          >
            {children}
          </form>
        ) : (
          children
        )}
      </Form>
    </AdminStructureGeneralDataFormContext.Provider>
  );
};
